import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page, Header, Content, ErrorPanel } from '@backstage/core-components';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { ResizableBox } from 'react-resizable';
import { useAnalytics, useApi } from '@backstage/core-plugin-api';
import { useAsync } from '@react-hookz/web';
import { usePermission } from '@backstage/plugin-permission-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import qs from 'qs';

import { scaffolderApiRef, useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import { DefaultTemplateOutputs, TaskSteps } from '@backstage/plugin-scaffolder-react/alpha';
import { AutoFollowTaskLogStream } from './AutoFollowTaskLogStream';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { taskCancelPermission, taskReadPermission, taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';

const useStyles = makeStyles(theme => ({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  retryButton: {
    marginRight: theme.spacing(1),
  },
  logsVisibilityButton: {
    marginRight: theme.spacing(1),
  },
  stepperInteractive: {
    '& [data-testid="step-label"]': {
      cursor: 'pointer',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1),
      transition: 'background-color 150ms ease, box-shadow 150ms ease',
    },
    '& [data-testid="step-label"]:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '& [data-testid="step-label"]:active': {
      backgroundColor: theme.palette.action.selected,
    },
    '& [data-testid="step-label"]:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
}));

export const CustomTaskPage: React.FC<{ TemplateOutputsComponent?: React.ComponentType<{ output?: any }>, children?: React.ReactNode }> = (props) => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const scaffolderApi = useApi(scaffolderApiRef);
  const taskStream = useTaskEventStream(taskId!);
  const classes = useStyles();
  const templateRouteRef = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const stepperRef = useRef<HTMLDivElement | null>(null);

  const steps = useMemo(
    () => taskStream.task?.spec.steps.map(step => ({
      ...step,
      ...taskStream?.steps?.[step.id],
    })) ?? [],
    [taskStream],
  );

  const [logsVisible, setLogVisibleState] = useState<boolean>(true);
  const [buttonBarVisible, setButtonBarVisibleState] = useState<boolean>(true);
  const [followActiveStep, setFollowActiveStep] = useState<boolean>(true);
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (taskStream.error) {
      setLogVisibleState(true);
    }
  }, [taskStream.error]);

  useEffect(() => {
    if (taskStream.completed && !taskStream.error) {
      setButtonBarVisibleState(false);
    }
  }, [taskStream.error, taskStream.completed]);

  const { allowed: canCancelTask } = usePermission({ permission: taskCancelPermission });
  const { allowed: canReadTask } = usePermission({ permission: taskReadPermission });
  const { allowed: canCreateTask } = usePermission({ permission: taskCreatePermission });

  const canStartOver = canReadTask && canCreateTask;

  const activeStep = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].status !== 'open') {
        return i;
      }
    }
    return 0;
  }, [steps]);

  // Keep selected step in sync with the active step while following
  useEffect(() => {
    if (followActiveStep) {
      setSelectedStepId(steps[activeStep]?.id);
    }
  }, [followActiveStep, steps, activeStep]);

  const isRetryableTask = taskStream.task?.spec.EXPERIMENTAL_recovery?.EXPERIMENTAL_strategy === 'startOver';
  const canRetry = canReadTask && canCreateTask && isRetryableTask;

  const startOver = useCallback(() => {
    const meta = (taskStream.task?.spec.templateInfo?.entity?.metadata ?? {}) as { namespace?: string; name?: string };
    const namespace = meta.namespace ?? '';
    const name = meta.name ?? '';
    const formData = taskStream.task?.spec.parameters ?? {};
    if (!namespace || !name) {
      return;
    }
    analytics.captureEvent('click', `Task has been started over`);
    navigate({
      pathname: templateRouteRef({ namespace, templateName: name }),
      search: `?${qs.stringify({ formData: JSON.stringify(formData) })}`,
    });
  }, [analytics, navigate, taskStream.task?.spec.parameters, taskStream.task?.spec.templateInfo?.entity?.metadata, templateRouteRef]);

  const [{ status: _retryStatus }, { execute: triggerRetry }] = useAsync(async () => {
    if (taskId) {
      analytics.captureEvent('retried', 'Template has been retried');
      if (scaffolderApi.retry) {
        await scaffolderApi.retry(taskId);
      }
    }
  });

  const [{ status: cancelStatus }, { execute: triggerCancel }] = useAsync(async () => {
    if (taskId) {
      analytics.captureEvent('cancelled', 'Template has been cancelled');
      await scaffolderApi.cancelTask(taskId);
    }
  });

  const Outputs = props.TemplateOutputsComponent ?? DefaultTemplateOutputs;
  const templateName = taskStream.task?.spec.templateInfo?.entity?.metadata.name || '';
  const cancelEnabled = !(taskStream.cancelled || taskStream.completed);

  return (
    <Page themeId="website">
      <Header
        pageTitleOverride={templateName ? `Task · ${templateName}` : 'Task'}
        title={<div>Task <code>{templateName}</code></div>}
        subtitle={`Task ID: ${taskId ?? ''}`}
      />
      <Content className={classes.contentWrapper}>
        {taskStream.error ? (
          <Box paddingBottom={2}>
            <ErrorPanel error={taskStream.error} titleFormat="markdown" title={taskStream.error.message} />
          </Box>
        ) : null}

        {buttonBarVisible ? (
          <Box paddingBottom={2}>
            <Paper>
              <Box padding={2}>
                <div className={classes.buttonBar}>
                  <Button
                    className={classes.cancelButton}
                    disabled={!cancelEnabled || (cancelStatus !== 'not-executed' && !isRetryableTask) || !canCancelTask}
                    onClick={triggerCancel}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  {isRetryableTask ? (
                    <Button
                      className={classes.retryButton}
                      disabled={cancelEnabled || !canRetry}
                      onClick={triggerRetry}
                      data-testid="retry-button"
                    >
                      Retry
                    </Button>
                  ) : null}
                  <Button
                    className={classes.logsVisibilityButton}
                    color="primary"
                    variant="outlined"
                    onClick={() => setLogVisibleState(!logsVisible)}
                  >
                    {logsVisible ? 'Hide logs' : 'Show logs'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={cancelEnabled || !canStartOver}
                    onClick={startOver}
                    data-testid="start-over-button"
                  >
                    Start over
                  </Button>
                </div>
              </Box>
            </Paper>
          </Box>
        ) : null}

        <Box paddingBottom={2}>
          <div
            ref={stepperRef}
            className={classes.stepperInteractive}
            onClick={(e) => {
              try {
                const container = stepperRef.current;
                if (!container) return;
                const target = e.target as HTMLElement | null;
                if (!target) return;
                const clickedLabel = target.closest('[data-testid="step-label"]') as HTMLElement | null;
                if (!clickedLabel) return;
                const labels = Array.from(container.querySelectorAll<HTMLElement>('[data-testid="step-label"]'));
                const index = labels.indexOf(clickedLabel);
                if (index < 0) return;
                const step = steps[index];
                if (!step?.id) return;
                setFollowActiveStep(false);
                setSelectedStepId(step.id);
                setLogVisibleState(true);
              } catch (_) {
                // noop
              }
            }}
          >
            <TaskSteps
              steps={steps}
              activeStep={activeStep}
              isComplete={taskStream.completed}
              isError={Boolean(taskStream.error)}
            />
          </div>
        </Box>

        {logsVisible ? (
          <ResizableBox height={500} minConstraints={[0, 160]} axis="y">
            <Paper style={{ height: '100%' }}>
              <Box padding={2} height="100%">
                {(() => {
                  const effectiveStepId = followActiveStep ? steps[activeStep]?.id : selectedStepId;
                  const shouldFollow = effectiveStepId === steps[activeStep]?.id;
                  const effectiveStepIndex = effectiveStepId ? steps.findIndex(step => step.id === effectiveStepId) : -1;
                  const effectiveStep = effectiveStepIndex >= 0 ? steps[effectiveStepIndex] : undefined;
                  const stepTitle = effectiveStep
                    ? `Step ${effectiveStepIndex + 1}: ${effectiveStep.name ?? effectiveStep.id}`
                    : 'Step logs';
                  return (
                    <AutoFollowTaskLogStream
                      logs={taskStream.stepLogs}
                      activeStepId={effectiveStepId}
                      follow={shouldFollow}
                      followActiveStep={followActiveStep}
                      onToggleFollow={() => setFollowActiveStep(!followActiveStep)}
                      headerTitle={
                        <Typography variant="subtitle2" color="textSecondary">
                          {stepTitle}
                        </Typography>
                      }
                    />
                  );
                })()}
              </Box>
            </Paper>
          </ResizableBox>
        ) : null}

        <Outputs output={taskStream.output} />
      </Content>
    </Page>
  );
};

export default CustomTaskPage;